import React, {useEffect, useMemo, useState} from 'react';
import ContentPasteOffRoundedIcon from '@mui/icons-material/ContentPasteOffRounded';
import {useNavigate, useOutletContext, useParams} from 'react-router-dom';
import AdmicityStatus from '../../../../../shared-components/AdmicityStatus';
import {
    useGetFormsByStudentQuery,
    useGetCommunicationFormsByStudentQuery,
    useGetStudentNotificationFormsQuery,
    useLazyGetStudentNotificationFormResponsesQuery
} from '../../../../../api/services/formRequestsService';
import AdmicityTableV2 from '../../../../../shared-components/Table/V2/AdmicityTableV2';
import {formatDate, formatFormExpirationDate} from '../../../../../utility/dateUtil';
import withPaginationV2 from '../../../../../hoc/pagination/withPaginationV2';
import {getFormRequestStatusInfo} from '../../../RequestAndResponses/common/common';
import FILTER_OPERATIONS from '../../../../../constants/filterOperations';
import {FORM_REQUEST_STATUSES, FORM_REQUEST_STATUSES_NAMES} from '../../../../../constants/formRequestStatuses';
import {
    downloadFormResponsePdfReport,
    useLazyGenerateFormResponsesReportByStudentQuery,
} from '../../../../../api/services/filesService';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import useFileDownloader from '../../../../../utility/hooks/useFileDownloader';
import useNotification from '../../../../../utility/hooks/useNotification';
import AdmicityTabs from '../../../../../shared-components/AdmicityTabs';
import {Box, Paper, useMediaQuery, useTheme} from '@mui/material';
import SelfServiceForms from './SelfServiceForms';
import UnderTab from './UnderTab';
import {COMPACT_TABLE_PROPS, communicationRowButtons, formRowButtons, openCommunicationRow, openFormRow, ROWS_HEIGHT_TABLE_PROPS, SUB_TAB_SX, withHyphenWhenEmpty, withToolbarAboveOnSmallScreen} from './formsTableStyle';
import useUser from '../../../../../utility/hooks/useUser';
import FORM_AUDIENCE_TYPE from '../../../../../constants/FormAudienceType';
import {getApprovalStatusInfo} from '../../../../../constants/FormApprovalStatuses';

/* eslint-disable react/prop-types */

const formsTableColumns = withHyphenWhenEmpty([
    {
        accessorKey: 'form.name',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS,
    },
    {
        accessorKey: 'status',
        header: 'Status',
        // Maria, 18 October 2026: a form not yet emailed ('Created') shows as 'Awaiting submission', as it does
        // once the email has gone
        Cell: ({cell}) => <AdmicityStatus {...getFormRequestStatusInfo(cell.getValue() === FORM_REQUEST_STATUSES.created ? FORM_REQUEST_STATUSES.sent : cell.getValue())}/>,
        filterVariant: 'select',
        filterFn: FILTER_OPERATIONS.EQUALS,
        filterSelectOptions: Object.values(FORM_REQUEST_STATUSES)
            .filter(statusId => statusId !== FORM_REQUEST_STATUSES.created)
            .map(statusId => ({
                value: statusId,
                label: FORM_REQUEST_STATUSES_NAMES[statusId]
            })),
    },
    {
        accessorKey: 'expirationDate',
        // Maria, 18 October 2026: 'Expiry', without the information pop-up
        header: 'Expiry',
        Cell: ({cell}) => cell.getValue() ? formatFormExpirationDate(cell.getValue()) : '',
        enableColumnFilter: false
    },
    {
        id: 'editedBy',
        accessorFn: (row) => row?.editedBy?.fullname ?? '',
        header: 'Submitted By',
        enableColumnFilter: false
    },
    {
        accessorKey: 'editedAt',
        header: 'Submitted At',
        Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStage',
        header: 'Approval Stage',
        Cell: ({row}) => {
            return row.original?.approvalStage ?? '';
        },
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStageProgress',
        header: 'Approval Progress',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStatus',
        header: 'Approval Status',
        Cell: ({cell: {getValue}}) => {
            const value = getValue();
            if (!value) return '';
            return <AdmicityStatus {...getApprovalStatusInfo(value)} />;
        },
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvedBy',
        header: 'Approved By',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvedAt',
        header: 'Approved At',
        Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalNote',
        header: 'Approval Note',
        enableColumnFilter: false,
    },
]);

const communicationFormsTableColumns = [
    {
        accessorKey: 'formName',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS,
    },
    {
        accessorKey: 'sentAt',
        header: 'Date',
        Cell: ({cell}) => {
            const date = cell.getValue();
            return date ? formatDate(date) : '';
        },
        enableColumnFilter: false,
    },
];

/* eslint-enable react/prop-types */
// Maria, 18 October 2026: the Communications list starts directly under its tab, as Self-service Forms does
// Maria, 18 October 2026: a parent looking at their child's profile sees whether a form has been approved, but not the
// other approval columns
const PARENT_HIDDEN_COLUMNS = ['approvalStage', 'approvalStageProgress', 'approvedBy', 'approvedAt', 'approvalNote'];

const StudentForms = () => {
    const {user} = useUser();
    const isParentView = !!user?.isParent?.() && !user?.hasElevatedAccess?.();
    /* eslint-disable react/prop-types */
    // Maria, 18 October 2026: staff see which forms parents cannot see. A parent sees a form they complete for their
    // child, or a form linked to students; any other form shows 'Not visible to parents' under its name.
    const formsColumnsForViewer = useMemo(() => (isParentView
        ? formsTableColumns.filter(column => !PARENT_HIDDEN_COLUMNS.includes(column.id ?? column.accessorKey))
        : formsTableColumns.map(column => (column.accessorKey === 'form.name'
            ? {
                ...column,
                Cell: ({row}) => {
                    const form = row.original.form ?? {};
                    const visibleToParents = form.audienceType === FORM_AUDIENCE_TYPE.parentsForChildCompletion || !!form.linkToStudents;
                    return (
                        <Box>
                            <Box>{form.name ?? row.original.formName}</Box>
                            {!visibleToParents && (
                                <Box sx={{fontSize: '0.75rem', color: 'text.secondary'}} data-testid="not-visible-to-parents">Not visible to parents</Box>
                            )}
                        </Box>
                    );
                }
            }
            : column))), [isParentView]);
    /* eslint-enable react/prop-types */
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(`${theme.breakpoints.down('sm')}, (max-height:600px)`);
    const downloadFile = useFileDownloader();
    const {showSuccessNotification} = useNotification();
    const navigate = useNavigate();
    const {studentId} = useParams();
    const studentIdNumber = Number(studentId);
    const outletContext = useOutletContext();
    const [currentTabIndex, setCurrentTabIndex] = useState(outletContext.formsState?.currentTabIndex ?? 0);
    const [selectedNotificationFormIndex, setSelectedNotificationFormIndex] = useState(outletContext.formsState?.selectedNotificationFormIndex);
    const {
        data: notificationForms = [],
        isLoading: isLoadingNotificationForms,
        isFetching: isFetchingNotificationForms,
    } = useGetStudentNotificationFormsQuery(studentId, {skip: currentTabIndex !== 1});
    const [generateFormResponsesReportByStudent] = useLazyGenerateFormResponsesReportByStudentQuery();
    const Table = useMemo(() => withPaginationV2(AdmicityTableV2, {
        func: useGetFormsByStudentQuery,
        props: {studentId}
    }), [studentId]);
    const CommunicationFormsTable = useMemo(() => withPaginationV2(AdmicityTableV2, {
        func: useGetCommunicationFormsByStudentQuery,
        props: {studentId}
    }), [studentId]);

    useEffect(() => {
        return () => {
            outletContext.setFormsState({currentTabIndex, selectedNotificationFormIndex});
        };
    }, [outletContext.setFormsState, currentTabIndex, selectedNotificationFormIndex]);

    const downloadPdfReport = async (formRequestId = null, formId = null, isBulk = true) => {
        if (formRequestId && !isBulk) {
            await downloadFile(
                downloadFormResponsePdfReport,
                {
                    formId,
                    formRequestId
                });
        } else {
            await generateFormResponsesReportByStudent({formId, studentId});
            showSuccessNotification('The PDFs is being created. It will be sent to your email address when the process is complete.');
        }
    };

    // AD-2735: on a phone the three sub-tabs are a little smaller, so each name fits on the screen
    const subTabSx = isSmallScreen ? {...SUB_TAB_SX, minWidth: 0, px: 1, fontSize: 12} : SUB_TAB_SX;
    const visibleFormsColumns = formsColumnsForViewer;
    const rowButtons = formRowButtons({
        open: request => navigate(`${request.id}`),
        compact: isSmallScreen
    });

    const tabs =
        [
            {
                label: 'Forms',
                tabSx: subTabSx,
                component: <Table
                    variant="elevation"
                    id={outletContext?.key}
                    columns={visibleFormsColumns}
                    tableProps={{
                        // AD-2735: on a small screen the icons are above the table, clear of the tabs
                        ...withToolbarAboveOnSmallScreen(COMPACT_TABLE_PROPS, isSmallScreen),
                        // the buttons are in the actions column, which stays in view at the right
                        enableRowActions: true,
                        displayColumnDefOptions: {'mrt-row-actions': rowButtons},
                        enableSorting: false,
                        enableFullScreenToggle: false,
                        enableStickyFooter: false,
                        muiTablePaperProps: {
                            sx: {
                                display: 'block',
                                flexGrow: 0,
                                height: 'auto',
                            }
                        },
                        // the shared container settings, so the columns are fitted to the page (formsTableStyle.js)
                        muiTableContainerProps: {
                            ...COMPACT_TABLE_PROPS.muiTableContainerProps,
                            sx: {
                                flex: 'unset',
                            }
                        }
                    }}
                    toolbarIconActions={
                        [{
                            icon: <FileDownloadRoundedIcon/>,
                            title: 'Download report',
                            type: 'select',
                            hideIfEmpty: true,
                            menuItems: [
                                {
                                    label: 'Download PDF',
                                    onClick: downloadPdfReport
                                },
                            ]
                        }]
                    }
                    onRowClick={(params) => openFormRow(params, request => navigate(`${request.id}`), isSmallScreen)}
                    noRowsOverlay={{
                        icon: <ContentPasteOffRoundedIcon/>,
                        text: 'No requests and responses yet'
                    }}
                />
            },
            {
                label: 'Self-service Forms',
                tabSx: subTabSx,
                component:
                    <SelfServiceForms
                        forms={notificationForms}
                        isLoading={isLoadingNotificationForms || isFetchingNotificationForms}
                        openIndex={selectedNotificationFormIndex}
                        setOpenIndex={setSelectedNotificationFormIndex}
                        navigate={navigate}
                        personId={studentIdNumber}
                        fetchFn={useLazyGetStudentNotificationFormResponsesQuery}
                        // Maria, 20 October 2026: the full width of the page, as Forms and Communications are
                        fill
                    />
            },
            {
                label: 'Communications',
                tabSx: subTabSx,
                component: <UnderTab tabTestId="tab-communications">
                    <CommunicationFormsTable
                    variant="elevation"
                    id={`${outletContext?.key}/communication`}
                    columns={communicationFormsTableColumns}
                    tableProps={{
                        ...COMPACT_TABLE_PROPS,
                        enableSorting: false,
                        enableColumnResizing: false,
                        enableTopToolbar: false,
                        // Maria, 19 October 2026: a heading row, as on the other lists
                        enableRowActions: true,
                        positionActionsColumn: 'last',
                        displayColumnDefOptions: {'mrt-row-actions': communicationRowButtons()},
                        // AD-2734: the table is as tall as its rows on every screen size
                        ...ROWS_HEIGHT_TABLE_PROPS,
                    }}
                    onRowClick={(params) => openCommunicationRow(params, communication => navigate(`${communication.requestId}`), isSmallScreen)}
                    noRowsOverlay={{
                        icon: <ContentPasteOffRoundedIcon/>,
                        text: 'No communication yet'
                    }}
                    />
                </UnderTab>
            },
        ];

    return (
        <Paper
            variant="outlined"
            sx={
                isSmallScreen
                    // the toolbar icons are placed on the row of sub-tabs, so the box keeps its position on a small screen
                    ? {position: 'relative'}
                    : {
                        // Maria, 18 October 2026: a white box the height of its table, like the other tabs
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 2,
                        borderColor: '#e3e8ef',
                        overflow: 'hidden'
                    }}
        >
            <AdmicityTabs
                tabs={tabs}
                tabIndex={currentTabIndex}
                onChange={(index) => {
                    setCurrentTabIndex(index);
                    setSelectedNotificationFormIndex(undefined);
                }}
                displayDivider
                hideIndicator
                slots={{
                    panel: {
                        paddingRight: 0,
                        paddingLeft: 0
                    }
                }}
            />
        </Paper>

    );
};

export default StudentForms;
